import React, { useEffect, useState } from 'react';
import { POWERUP_LABELS } from '../game/constants';
import { RoomCodeDisplay } from './RoomCodeDigits';
import BoardGrid from './BoardGrid';
import undoIcon from '../assets/undo.svg';
import switchIcon from '../assets/switch.svg';
import trashIcon from '../assets/trashcan.svg';
import freezeIcon from '../assets/freeze-powerup.svg';
import switchPowerupIcon from '../assets/switch-powerup.svg';
import garbagePowerupIcon from '../assets/garbage-powerup.svg';
import removePowerupIcon from '../assets/remove-powerup.svg';
import freezePowerupIcon from '../assets/freeze-powerup.svg';
import colorPowerupIcon from '../assets/color-powerup.svg';

const GameView = ({
  score,
  best,
  roomCode,
  connectionStatus,
  codeLength,
  opponent,
  opponentFreezeSeconds,
  tileSizeVars,
  opponentTileSizeVars,
  waitingForStart,
  targetingOpponent,
  onOpponentCellClick,
  isHost,
  canStart,
  handleStartGame,
  handleNewGame,
  tiles,
  handlePointerStart,
  handlePointerEnd,
  handlePointerCancel,
  isFrozen,
  freezeSeconds,
  gameOver,
  isWinner,
  isLoser,
  powerups,
  colorShiftMap,
  opponentColorShiftMap,
  undoableMoves,
  selectingDeleteTile,
  selectingSwitch,
  switchFirst,
  onDeleteTileCellClick,
  onSwitchTilesClick,
  soloMode,
  triggerPowerup,
  lastEvent,
  onLeaveLobby,
}) => {
  const initialMobile = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(max-width: 640px)').matches
    : false;
  const [isMobile, setIsMobile] = useState(initialMobile);
  const [showAdModal, setShowAdModal] = useState(initialMobile);
  const [deleteHover, setDeleteHover] = useState(null);
  const [switchHover, setSwitchHover] = useState(null);
  const [alertText, setAlertText] = useState('');
  const [alertVisible, setAlertVisible] = useState(false);
  const roundEnded = !soloMode && (isWinner || isLoser || gameOver || opponent?.gameOver);
  const showNewGameButton = isHost && roundEnded && typeof handleNewGame === 'function';
  const opponentResultLabel = !roundEnded
    ? null
    : isWinner
      ? 'Loser'
      : isLoser
        ? 'Winner'
        : gameOver
          ? 'Winner'
          : 'Loser';
  const boardPowerupTypes = ['undo', 'switchTiles', 'deleteTile'];
  const otherPowerupTypes = Object.keys(POWERUP_LABELS)
    .filter((type) => !boardPowerupTypes.includes(type))
    .filter((type) => type !== 'targetedGarbage');

  const freezeStylePowerups = ['freeze', 'shuffle', 'garbage', 'clear', 'targetedGarbage'];

  const enemyPowerupButtons = ['freeze', 'shuffle', 'garbage', 'clear', 'colors'];
  const enemyPowerupButtonIcons = {
    freeze: freezePowerupIcon,
    shuffle: switchPowerupIcon,
    garbage: garbagePowerupIcon,
    clear: removePowerupIcon,
    colors: colorPowerupIcon,
  };
  const renderPowerupButton = (type) => {
    const boardLocked = undoableMoves < 2;
    const available =
      type === 'undo'
        ? Math.min(powerups[type] || 0, undoableMoves || 0)
        : powerups[type] || 0;
    const isDisabled =
      waitingForStart ||
      available === 0 ||
      (type === 'targetedGarbage' && targetingOpponent) ||
      (boardPowerupTypes.includes(type) && boardLocked);

    if (enemyPowerupButtons.includes(type)) {
      const label = POWERUP_LABELS[type] || 'Powerup';
      const iconSrc = enemyPowerupButtonIcons[type] || freezeIcon;
      return (
        <button
          key={type}
          type="button"
          onClick={() => triggerPowerup(type)}
          disabled={isDisabled}
          className={`transition-transform ${isDisabled ? 'cursor-not-allowed opacity-60' : 'hover:translate-y-[-1px]'}`}
        >
          <div className="relative w-[120px] h-[50px] md:w-[132px] md:h-[56px]">
            <div
              className="absolute inset-0 rounded-[16px] border-[4px] shadow-md"
              style={{ backgroundColor: '#F67C5F', borderColor: '#413C35' }}
            />
            <div className="relative z-10 h-full px-2 md:px-2.5 flex items-center gap-1 text-[#413C35]">
              <img
                src={iconSrc}
                alt={label}
                className="w-[22px] h-[22px] md:w-[26px] md:h-[26px]"
                draggable="false"
              />
              <div className="flex-1 flex flex-col items-end text-right leading-tight">
                <div className="text-[13px] md:text-sm font-extrabold leading-tight">{label}</div>
                <div className="text-sm md:text-base font-extrabold mt-0 leading-tight">{available}</div>
              </div>
            </div>
          </div>
        </button>
      );
    }

    const renderIconTileButton = (iconSrc, label, { shiftX = 0, shiftY = 0 } = {}) => (
      <button
        key={type}
        type="button"
        onClick={() => triggerPowerup(type)}
        disabled={isDisabled}
        className={`transition-transform ${isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:translate-y-[-1px]'}`}
      >
        <div
          className="relative w-[96px] h-[48px] md:w-[110px] md:h-[54px] flex items-center justify-center rounded-[16px] outline outline-[5px] shadow-md"
          style={{ backgroundColor: '#F67C5F', outlineColor: '#413C35' }}
        >
          <img src={iconSrc} alt={label} className="w-[52%] h-[52%] object-contain" draggable="false" />
          <span
            className="absolute inset-0 flex items-center justify-center text-sm md:text-base font-extrabold"
            style={{ color: '#413C35', transform: `translate(${shiftX}px, ${shiftY}px)` }}
          >
            {available}
          </span>
        </div>
      </button>
    );

    if (type === 'undo') return renderIconTileButton(undoIcon, 'Undo', { shiftX: 2, shiftY: 0 });
    if (type === 'switchTiles') return renderIconTileButton(switchIcon, 'Switch');
    if (type === 'deleteTile') return renderIconTileButton(trashIcon, 'Delete Tile', { shiftY: 2 });

    return (
      <button
        key={type}
        type="button"
        onClick={() => triggerPowerup(type)}
        disabled={isDisabled}
        className={`rounded-full px-4 py-2 text-sm font-semibold border-[3px] border-black transition-all ${
          isDisabled ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-black text-white hover:translate-y-[-1px]'
        }`}
      >
        {POWERUP_LABELS[type]} ({available})
      </button>
    );
  };

  useEffect(() => {
    const checkMobile = () => (window.matchMedia ? window.matchMedia('(max-width: 640px)').matches : false);
    const update = () => setIsMobile(checkMobile());
    update();
    const mql = window.matchMedia ? window.matchMedia('(max-width: 640px)') : null;
    mql?.addEventListener('change', update);
    return () => mql?.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setShowAdModal(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!selectingDeleteTile) setDeleteHover(null);
    if (!selectingSwitch) setSwitchHover(null);
  }, [selectingDeleteTile, selectingSwitch]);

  useEffect(() => {
    if (!lastEvent) {
      setAlertVisible(false);
      setAlertText('');
      return;
    }
    setAlertText(lastEvent);
    setAlertVisible(true);
    const hide = setTimeout(() => setAlertVisible(false), 1500);
    const clear = setTimeout(() => setAlertText(''), 2200);
    return () => {
      clearTimeout(hide);
      clearTimeout(clear);
    };
  }, [lastEvent]);

  const highlightCells = [];
  if (selectingDeleteTile && deleteHover) highlightCells.push(deleteHover);
  if (selectingSwitch) {
    if (switchFirst) highlightCells.push(switchFirst);
    if (switchHover) highlightCells.push(switchHover);
  }

  return (
    <div className="flex flex-col items-center justify-center gap-1 md:gap-1.5">
      <div className="w-full flex items-center justify-between mb-1 px-2.5 md:px-4 gap-2 md:gap-3">
      <img
        src="/logo192.png"
        alt="2048 Logo"
        className="w-[110px] md:w-[140px] aspect-square rounded-2xl bg-white p-2 shadow-sm object-contain"
        draggable="false"
      />
      <div className="flex-1 flex flex-col items-center select-none">
        <div className="flex flex-col items-center gap-1 text-base md:text-lg font-semibold text-[#6b7280]">
          <span className="text-3xl md:text-4xl text-black leading-tight">{score}</span>
          {soloMode ? <span className="text-sm md:text-base">Highscore: {best}</span> : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onLeaveLobby}
        className="rounded-full border-[3px] border-black px-5 py-2 text-sm font-semibold bg-white hover:bg-gray-100 transition-colors shadow-sm"
        >
          Zur Lobby
        </button>
      </div>
      {boardPowerupTypes.length ? (
        <div className="w-full flex flex-wrap justify-center gap-5 px-8 md:px-10 mt-3 mb-3">
          {boardPowerupTypes.map(renderPowerupButton)}
        </div>
      ) : null}
      <div className="flex flex-col items-center gap-1">
        <div className="relative flex items-center justify-center">
          {!soloMode && (
            <div
              className="absolute top-1/2 -translate-y-1/2 flex flex-col items-start gap-2"
            style={{ right: 'calc(100% + 20px)' }}
          >
            <div className="flex flex-col items-end gap-2 w-full px-3 md:px-4">
              <div className="flex items-baseline gap-2 w-full">
                <div className="text-xl md:text-2xl text-black font-semibold flex-1">
                  {opponent?.name || 'Gegner'}
                </div>
                {opponent ? (
                  <div className="text-sm text-[#6b7280] whitespace-nowrap text-right">
                    Score: {opponent.score ?? 0}
                  </div>
                ) : null}
              </div>
              <div className="bg-[#f5f5f5] rounded-[24px] border-[4px] border-black p-3 shadow-sm flex items-center justify-center relative">
                <BoardGrid
                  tiles={opponent?.tiles || []}
                  sizeVars={opponentTileSizeVars}
                  className="scale-90"
                  colorMap={opponentColorShiftMap}
                  muted={!opponent}
                  pointerEvents={targetingOpponent ? 'auto' : 'none'}
                  onCellClick={targetingOpponent ? onOpponentCellClick : undefined}
                  overlay={
                    opponent ? (
                      roundEnded ? (
                        <div className="absolute inset-0 z-10 bg-black/30 rounded-[24px] flex items-center justify-center pointer-events-none">
                          <div className="text-white text-xl font-bold drop-shadow">{opponentResultLabel}</div>
                        </div>
                      ) : opponentFreezeSeconds > 0 ? (
                        <div className="absolute inset-0 z-10 bg-black/25 rounded-[24px] flex items-center justify-center pointer-events-none">
                          <div className="text-white text-lg font-semibold drop-shadow">
                            Freeze {opponentFreezeSeconds}s
                          </div>
                        </div>
                      ) : targetingOpponent ? (
                        <div className="absolute inset-0 z-10 bg-black/20 rounded-[24px] flex items-center justify-center pointer-events-none">
                          <div className="text-black text-sm font-semibold drop-shadow">Feld anklicken für Garbage</div>
                        </div>
                      ) : null
                    ) : roomCode && isHost ? (
                      <div
                        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-sm text-[#6b7280] pointer-events-none rounded-[24px]"
                        style={{ backgroundColor: 'rgba(245,245,245,0.88)' }}
                      >
                        <div className="text-black font-semibold">Room-Code</div>
                        <RoomCodeDisplay code={roomCode} length={codeLength} />
                      </div>
                    ) : null
                  }
                />
              </div>
            </div>
          </div>
        )}
        {!isMobile && (
          <div
            className="absolute top-1/2 -translate-y-1/2 flex flex-col items-start gap-2"
            style={{ left: 'calc(100% + 200px)' }}
          >
            <div className="w-[120px] md:w-[160px] h-[360px] md:h-[420px] bg-[#fef3c7] border-[4px] border-black rounded-[20px] shadow-sm flex items-center justify-center text-[#92400e] text-sm font-semibold select-none">
              Platzhalter
            </div>
          </div>
        )}
        <div className="bg-[#f5f5f5] rounded-[28px] p-2 md:p-3 shadow-sm flex flex-col gap-1.5">
          <BoardGrid
            tiles={waitingForStart ? [] : tiles}
            sizeVars={tileSizeVars}
            colorMap={colorShiftMap}
            onCellClick={
              selectingDeleteTile
                ? onDeleteTileCellClick
                : selectingSwitch
                ? onSwitchTilesClick
                : undefined
            }
            onCellHover={
              selectingDeleteTile
                ? (r, c) => setDeleteHover({ row: r, col: c })
                : selectingSwitch
                ? (r, c, occupied) => setSwitchHover(occupied ? { row: r, col: c } : null)
                : undefined
            }
            dimOthers={selectingDeleteTile || selectingSwitch}
            highlightCells={highlightCells}
            className={`${
              waitingForStart || isFrozen || gameOver || opponent?.gameOver || isWinner || isLoser ? 'opacity-60' : ''
            }`}
            overlay={
              <>
                {selectingDeleteTile && (
                  <div className="absolute inset-0 z-20 rounded-[28px] flex items-center justify-center pointer-events-none">
                    <div className="text-black text-sm md:text-base font-semibold drop-shadow">
                      Feld anklicken für Delete Tile
                    </div>
                  </div>
                )}
                {selectingSwitch && (
                  <div className="absolute inset-0 z-20 rounded-[28px] flex items-center justify-center pointer-events-none">
                    <div className="text-black text-sm md:text-base font-semibold drop-shadow text-center px-4">
                      {switchFirst ? 'Zweites Tile anklicken zum Tauschen' : 'Erstes Tile anklicken zum Tauschen'}
                    </div>
                  </div>
                )}
                {waitingForStart && (
                  <div className="absolute inset-0 z-20 bg-black/35 rounded-[28px] flex flex-col items-center justify-center gap-3 text-white text-xl font-semibold">
                    {isHost ? (
                      <>
                        <div>Bereit?</div>
                        <button
                          type="button"
                          onClick={handleStartGame}
                          disabled={!canStart}
                          className={`rounded-full px-5 py-2 text-sm font-semibold border-[3px] border-black shadow-sm transition-colors ${
                            canStart
                              ? 'bg-[#fef3c7] text-black hover:brightness-[0.97]'
                              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          }`}
                        >
                          Spiel starten
                        </button>
                        {!canStart && <div className="text-sm text-[#d1d5db]">Warte auf zweiten Spieler</div>}
                      </>
                    ) : (
                      <div>Warten auf Start</div>
                    )}
                  </div>
                )}
                {isFrozen && (
                  <div className="absolute inset-0 z-10 bg-black/40 rounded-[28px] flex items-center justify-center pointer-events-none">
                    <div className="text-white text-3xl font-bold drop-shadow">Freeze {freezeSeconds}s</div>
                  </div>
                )}
                {!isFrozen && (gameOver || isLoser) && !isWinner && (
                  <div className={`absolute inset-0 z-10 bg-black/40 rounded-[28px] flex items-center justify-center ${showNewGameButton ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                    <div className="flex flex-col items-center gap-4">
                      <div className="text-white text-3xl font-bold drop-shadow">Loser</div>
                      {showNewGameButton ? (
                        <button
                          type="button"
                          onClick={handleNewGame}
                          disabled={!canStart}
                          className={`rounded-full px-5 py-2 text-sm font-semibold border-[3px] border-black shadow-sm transition-colors ${
                            canStart
                              ? 'bg-[#fef3c7] text-black hover:brightness-[0.97]'
                              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          }`}
                        >
                          Neues Spiel
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                {!isFrozen && isWinner && (
                  <div className={`absolute inset-0 z-10 bg-black/40 rounded-[28px] flex items-center justify-center ${showNewGameButton ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                    <div className="flex flex-col items-center gap-4">
                      <div className="text-white text-3xl font-bold drop-shadow">Winner</div>
                      {showNewGameButton ? (
                        <button
                          type="button"
                          onClick={handleNewGame}
                          disabled={!canStart}
                          className={`rounded-full px-5 py-2 text-sm font-semibold border-[3px] border-black shadow-sm transition-colors ${
                            canStart
                              ? 'bg-[#fef3c7] text-black hover:brightness-[0.97]'
                              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          }`}
                        >
                          Neues Spiel
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                {!isFrozen && opponent?.gameOver && !gameOver && !isWinner && (
                  <div className={`absolute inset-0 z-10 bg-black/40 rounded-[28px] flex items-center justify-center ${showNewGameButton ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                    <div className="flex flex-col items-center gap-4">
                      <div className="text-white text-3xl font-bold drop-shadow">Winner</div>
                      {showNewGameButton ? (
                        <button
                          type="button"
                          onClick={handleNewGame}
                          disabled={!canStart}
                          className={`rounded-full px-5 py-2 text-sm font-semibold border-[3px] border-black shadow-sm transition-colors ${
                            canStart
                              ? 'bg-[#fef3c7] text-black hover:brightness-[0.97]'
                              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          }`}
                        >
                          Neues Spiel
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </>
            }
            onPointerDown={handlePointerStart}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerCancel}
          />
        </div>
      </div>
        <div className="w-full flex flex-col items-center gap-1.5 mt-1.5 md:mt-2 pb-6 md:pb-8">
          {!soloMode && otherPowerupTypes.length ? (
            <div className="flex flex-wrap justify-center gap-2">
              {otherPowerupTypes.map(renderPowerupButton)}
            </div>
          ) : null}
          <div className="relative w-full min-h-[32px] flex justify-center pointer-events-none">
            {alertText ? (
              <div
                role="alert"
                className={`absolute top-0 left-1/2 -translate-x-1/2 transition-all duration-500 ${
                  alertVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                }`}
              >
                <div className="p-2 px-3 bg-[#EDC22E] items-center leading-none rounded-2xl flex shadow-lg border border-[#413C35]/20 text-[#413C35] min-w-[320px] md:min-w-[440px] max-w-[90vw]">
                  <span className="flex rounded-full bg-[#F67C5F] uppercase px-2 py-1 text-[10px] md:text-xs font-bold mr-3 whitespace-nowrap">
                    Info
                  </span>
                  <span className="font-semibold mr-2 text-left flex-auto text-sm md:text-base">{alertText}</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {isMobile && showAdModal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4">
          <div className="relative bg-white rounded-2xl border-[4px] border-black shadow-xl p-5 max-w-sm w-full text-center min-h-[80vh] max-h-[90vh]">
            <button
              type="button"
              onClick={() => setShowAdModal(false)}
              className="absolute top-3 right-3 h-9 w-9 rounded-full border-[3px] border-black bg-white text-black font-semibold hover:bg-gray-100 transition-colors"
              aria-label="Schließen"
            >
              ✕
            </button>
            <div className="text-lg font-semibold mb-2 mt-2">Platzhalter</div>
            <div className="text-sm text-[#6b7280] mb-4">Werbefläche für mobile Nutzer</div>
            <div className="h-full w-full bg-[#f5f5f5] border-[3px] border-dashed border-[#d1d5db] rounded-xl flex items-center justify-center text-[#9ca3af] text-sm">
              Anzeige
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameView;
